/**
 * @fileoverview `/write` and `/write/:naddr` -- the only route in this app that
 * needs a key.
 *
 * The edit URL holds the page's ADDRESS for the same reason the reading URL
 * does: it is the only name a page has that survives being revised. An edit
 * link built on an event id would break the first time the page was saved,
 * which is the one moment it is guaranteed to be used again.
 *
 * WHAT THIS ROUTE REFUSES, AND WHY EACH REFUSAL IS SPOKEN ALOUD. Someone else's
 * page, and a page stored in a shape this editor does not write. Both are dead
 * ends, and both are the kind of dead end that is infuriating when a screen
 * merely fails to work instead of saying what is wrong. This is the writing
 * side, where an author is entitled to the real reason; the reading side's
 * discipline of collapsing every failure into one answer protects a private
 * page's existence and has nothing to protect here.
 */

import type { SignerInterface } from '@cloistr/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PageEditor, emptyDraft, type EditorDraft } from '@/components/PageEditor';
import { RequireSigner } from '@/components/RequireSigner';
import { editPath } from '@/services/nostr/pageLinks';
import { PAGE_INDEX_KIND } from '@/services/pages/kinds';
import { loadPage } from '@/services/pages/loadPage';
import type { PageAddress } from '@/services/pages/publishPage';

export function EditorRoute() {
  const params = useParams();

  // CAPTURED ONCE, ON PURPOSE. Publishing a brand new page rewrites the URL to
  // that page's own address, so that a reload comes back to the page rather
  // than to a blank form. Re-reading the parameter would treat that rewrite as
  // a navigation and reload the editor out from under the author, a moment
  // after they pressed Publish.
  const [naddr] = useState<string | null>(params.naddr ?? null);

  return (
    <RequireSigner>
      {(signer, pubkey) => <Editing naddr={naddr} signer={signer} pubkey={pubkey} />}
    </RequireSigner>
  );
}

type Loaded =
  | { status: 'new' }
  | { status: 'ready'; draft: EditorDraft; address: PageAddress }
  | { status: 'unavailable' }
  | { status: 'not-yours'; naddr: string }
  | { status: 'unsupported-shape'; naddr: string };

function Editing({
  naddr,
  signer,
  pubkey,
}: {
  naddr: string | null;
  signer: SignerInterface;
  pubkey: string;
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(naddr === null ? { status: 'new' } : null);

  // THE RELAY'S CEILING IS NO LONGER FETCHED HERE. It was, so the editor could
  // warn an author approaching it while they still had something to do about it.
  // Every page's body is a blob since 2026-09-16 and the event carries a
  // fixed-length hash, so there is no draft a person could type that comes near
  // the limit. The publisher still measures the real event against the real
  // relay before signing; see the note in `PageEditor.tsx`.

  /**
   * THE SIGNER IS READ THROUGH A REF AND IS NOT A DEPENDENCY, which is the same
   * defence the two reading routes carry and is needed here for the same
   * measured reason. Once a session is restored, `@cloistr/auth` is in a
   * self-sustaining effect loop that mints a new signer on every turn of it --
   * the mechanism is spelled out in `PageRoute`. `RequireSigner` passes that
   * object straight through, so this prop is a new value forever. With it in the
   * dependency array the effect below re-runs forever, and it opens a relay
   * subscription each time.
   *
   * THIS ROUTE IS THE WORST PLACE FOR IT, because the signer is guaranteed
   * present: the gate above will not render this component without one. On the
   * reading routes the loop needed a reader who had signed in, which is the
   * uncommon case. Here EVERY author opening an existing page to edit it is in
   * it.
   *
   * `pubkey` is already a dependency and is the thing the load actually varies
   * with -- it decides whose page this is and whether a private one opens.
   */
  const signerRef = useRef(signer);
  signerRef.current = signer;

  useEffect(() => {
    if (naddr === null) return;
    let live = true;
    // The signer is always present on this route -- it is behind the author
    // gate -- and it is what lets an author open their OWN private page to edit
    // it. Without it the editor would show "not available" for a page the
    // person looking at it wrote.
    void loadPage(naddr, { signer: signerRef.current }).then((result) => {
      if (!live) return;
      setLoaded(classify(result, naddr, pubkey));
    });
    return () => {
      live = false;
    };
  }, [naddr, pubkey]);

  const onPublished = useCallback((address: PageAddress) => {
    // A new page has just acquired an address, and from here on every save is
    // an edit of it. Rewriting the URL with the history API rather than the
    // router is deliberate: the router would treat it as a navigation and
    // remount the editor, discarding the text still on screen. This only makes
    // the URL describe the state the page is already in.
    window.history.replaceState(null, '', editPath(address));
  }, []);

  if (loaded === null) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  if (loaded.status === 'unavailable') {
    return (
      <div className="page-unavailable">
        <h1>That page is not available</h1>
        <p className="page-unavailable-note">
          Nothing at that address could be opened for editing.
        </p>
      </div>
    );
  }

  if (loaded.status === 'not-yours') {
    return (
      <div className="page-unavailable">
        <h1>That page is somebody else&rsquo;s</h1>
        <p className="page-unavailable-note">
          A page can only be edited by the key that published it. You can{' '}
          <Link to={`/${loaded.naddr}`}>read it</Link> like anyone else.
        </p>
      </div>
    );
  }

  if (loaded.status === 'unsupported-shape') {
    return (
      <div className="page-unavailable">
        <h1>This page cannot be edited here</h1>
        <p className="page-unavailable-note">
          It is stored in a shape this editor does not write, so saving it would publish a
          second page at a different address rather than replace this one. You can still{' '}
          <Link to={`/${loaded.naddr}`}>read it</Link>.
        </p>
      </div>
    );
  }

  return (
    <PageEditor
      signer={signer}
      initial={loaded.status === 'new' ? emptyDraft() : loaded.draft}
      initialAddress={loaded.status === 'new' ? null : loaded.address}
      onPublished={onPublished}
    />
  );
}

function classify(
  result: Awaited<ReturnType<typeof loadPage>>,
  naddr: string,
  pubkey: string
): Loaded {
  if (result.status !== 'ready') return { status: 'unavailable' };

  const { index, body } = result;
  if (index.address.pubkey !== pubkey) return { status: 'not-yours', naddr };

  // THIS EDITOR WRITES EXACTLY ONE KIND, so a page stored under any other is one
  // it cannot save. Since 2026-09-16 that is every page we publish, private or
  // not, markdown or raw HTML -- the test used to depend on the format and on
  // whether a key came back, because a public markdown page was a long-form
  // event and the rest were ours.
  //
  // WHAT LANDS HERE NOW IS A LONG-FORM ARTICLE, which our READER renders happily
  // and this publisher will never produce: one of the author's own 30023s,
  // written in some other client, or one of ours from before the ruling. Saving
  // it would write a 33301, which is a different address, so the original would
  // stay published under the same name and the author would have made a second
  // page without being told. Refused out loud instead.
  if (index.address.kind !== PAGE_INDEX_KIND) {
    return { status: 'unsupported-shape', naddr };
  }

  // THE KEY'S PRESENCE IS WHAT SAYS THE PAGE IS PRIVATE, and it is the only
  // thing that can: the kind no longer distinguishes them, and by design there
  // is no `private` tag to read.
  const isPrivate = result.pageKey !== undefined;

  return {
    status: 'ready',
    address: index.address,
    draft: {
      identifier: index.address.identifier,
      title: index.title,
      summary: index.summary,
      format: index.format,
      body,
      private: isPrivate,
      // CARRIED, AND THE SAVE DEPENDS ON IT. A private page saved without its
      // key is refused by the publisher, because a fresh key would re-seal the
      // page against one nobody has been given and revoke every recipient
      // without a word on any screen.
      pageKey: result.pageKey ?? null,
      // The theme IS shown now and has a control; width and columns are still
      // carried and not shown. Republishing without them would quietly strip a
      // page of styling its author chose elsewhere, which is the same data loss
      // whether or not this screen happens to have an input for it.
      layout: index.layout,
      themeAddress: index.themeAddress,
      publishedAt: index.publishedAt,
    },
  };
}
